import { useEffect, useRef, useState } from 'react';
import { Puzzle } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { escucharPedidosDeEsx, type PedidoDeEsx } from '../lib/esxRequerido';
import { FileDrop } from './FileDrop';

/**
 * LA VENTANA QUE EXIGE EL .ESX (Manuel 2026-10-05). Bloqueante a propósito: sin botón de cerrar,
 * sin "no lo tengo", sin clic afuera ni Escape. Se abre cuando una pantalla acaba de guardar un
 * estimado nuestro y el servidor confirma que le falta su .ESX; se cierra sola al subirlo.
 */
type Estado = { estimateId: string | null; estimateName: string | null; hasEsx: boolean };

export function EsxRequiredHost() {
  const { t } = useI18n();
  const [cola, setCola] = useState<PedidoDeEsx[]>([]);
  const [estado, setEstado] = useState<Estado | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /* BYPASS CON MOTIVO (Manuel 2026-10-06): "cualquiera pueda bypass it pero tiene que poner el
     motivo y eso queda en el item del estimado… bypassed by user name y la razón". */
  const [bypassAbierto, setBypassAbierto] = useState(false);
  const [motivo, setMotivo] = useState('');
  const consultando = useRef<string | null>(null);

  useEffect(() => escucharPedidosDeEsx((p) => setCola((c) => [...c, p])), []);

  const actual = cola[0] ?? null;
  const terminar = () => { actual?.listo(); setEstado(null); setErr(null); setBypassAbierto(false); setMotivo(''); consultando.current = null; setCola((c) => c.slice(1)); };

  // Al tomar un pedido: ¿de verdad falta el ESX? (misma regla que el backend usa para frenar).
  useEffect(() => {
    if (!actual || consultando.current === actual.claimId) return;
    consultando.current = actual.claimId;
    api.get<Estado>(`/api/claims/${actual.claimId}/esx-status`)
      .then((r) => { if (!r.estimateId || r.hasEsx) terminar(); else setEstado(r); })
      // Si no se pudo preguntar no se bloquea a nadie a ciegas: el backend igual frena el avance.
      .catch(() => terminar());
  }, [actual]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape no cierra: se traga la tecla mientras la ventana está abierta.
  useEffect(() => {
    if (!estado) return;
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener('keydown', k, true);
    return () => window.removeEventListener('keydown', k, true);
  }, [estado]);

  async function subir(file: File) {
    if (!actual || !estado?.estimateId) return;
    if (!/\.esx$/i.test(file.name)) { setErr(`${t('esxReq.onlyEsx')} (${file.name})`); return; }
    setBusy(true); setErr(null);
    try {
      const key = await uploadFile(file, 'estimates');
      await api.post(`/api/files/${estado.estimateId}/estimate-esx`, { fileKey: key, fileName: file.name, mimeType: file.type || 'application/octet-stream' });
      window.dispatchEvent(new Event('esx:uploaded'));
      terminar();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  async function saltar() {
    if (!actual || !estado?.estimateId) return;
    const razon = motivo.trim();
    if (razon.length < 5) { setErr(t('esxReq.reasonRequired')); return; }
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${actual.claimId}/esx-bypass`, { reason: razon, estimateId: estado.estimateId });
      window.dispatchEvent(new Event('esx:uploaded'));
      terminar();
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  if (!actual || !estado) return null;
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="esx-req-title"
      style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)', background: 'rgba(0,0,0,0.6)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div className="card" style={{ width: 'min(520px, 100%)', display: 'grid', gap: 12, boxShadow: 'var(--shadow-lg, var(--shadow))' }}>
        <h2 id="esx-req-title" className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
          <Puzzle size={18} strokeWidth={1.75} /> {t('esxReq.title')}
        </h2>
        <div style={{ fontSize: 'var(--fs-sm)' }}>{t('esxReq.body')}</div>
        {estado.estimateName && (
          <div className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('esxReq.for')} <b>{estado.estimateName}</b></div>
        )}
        <FileDrop accept=".esx" busy={busy} busyLabel={t('esxReq.uploading')} label={t('esxReq.drop')} onFile={(f) => void subir(f)} />
        {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)' }}>{err}</div>}
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('esxReq.noSkip')}</div>
        {/* LA SALIDA CON NOMBRE: se puede saltar, pero diciendo por qué, y queda escrito en el estimado. */}
        <div style={{ borderTop: '1px dashed var(--line)', paddingTop: 10, display: 'grid', gap: 8 }}>
          {!bypassAbierto ? (
            <button type="button" className="btn ghost sm" style={{ justifySelf: 'start', color: 'var(--orange)' }} disabled={busy} onClick={() => setBypassAbierto(true)}>
              {t('esxReq.bypassBtn')}
            </button>
          ) : (
            <>
              <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 600, color: 'var(--orange)' }}>{t('esxReq.bypassTitle')}</div>
              <textarea className="input" value={motivo} onChange={(e) => setMotivo(e.target.value)} autoFocus
                placeholder={t('esxReq.bypassPh')} style={{ width: '100%', minHeight: 64, fontSize: 'var(--fs-xs)' }} />
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('esxReq.bypassHelp')}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn sm" style={{ background: 'var(--orange)', color: '#fff', borderColor: 'transparent' }} disabled={busy || motivo.trim().length < 5} onClick={() => void saltar()}>
                  {busy ? '…' : t('esxReq.bypassConfirm')}
                </button>
                <button type="button" className="btn ghost sm" disabled={busy} onClick={() => { setBypassAbierto(false); setMotivo(''); setErr(null); }}>{t('common.cancel')}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
